/**
 * Supabase Client Configuration
 * 
 * This module initializes and exports the Supabase client.
 * Make sure to set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
    console.warn(
        'Supabase credentials not found. Authentication features will be disabled. ' +
        'Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env.local'
    );
}

// Create Supabase client (or null if not configured)
export const supabase = supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: true
        }
    })
    : null;

// Helper to check if Supabase is configured
export const isSupabaseConfigured = (): boolean => {
    return supabase !== null;
};

// Database types (will be extended as we add tables)
export interface Database {
    public: {
        Tables: {
            profiles: {
                Row: {
                    id: string;
                    display_name: string | null;
                    avatar_url: string | null;
                    created_at: string;
                    updated_at: string;
                };
                Insert: {
                    id: string;
                    display_name?: string | null;
                    avatar_url?: string | null;
                    created_at?: string;
                    updated_at?: string;
                };
                Update: {
                    display_name?: string | null;
                    avatar_url?: string | null;
                    updated_at?: string;
                };
            };
            user_favorites: {
                Row: {
                    id: string;
                    user_id: string;
                    song_id: number;
                    created_at: string;
                };
                Insert: {
                    id?: string;
                    user_id: string;
                    song_id: number;
                    created_at?: string;
                };
                Update: {
                    song_id?: number;
                };
            };
            user_playlists: {
                Row: {
                    id: string;
                    user_id: string;
                    name: string;
                    songs: number[];
                    created_at: string;
                    updated_at: string;
                };
                Insert: {
                    id?: string;
                    user_id: string;
                    name: string;
                    songs?: number[];
                    created_at?: string;
                    updated_at?: string;
                };
                Update: {
                    name?: string;
                    songs?: number[];
                    updated_at?: string;
                };
            };
        };
    };
}
